// InviteBarterUsers.tsx
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import ProjectApi from "../../../../../../api/projectApi";
import SpecialistApi from "../../../../../../api/specialistsApi";
import { ReactComponent as CloseIcon } from "../../../../../../assets/General/close.svg";
import CircleAvatar from "../../../../../../components/CircleAvatar/CircleAvatar";
import Input from "../../../../../../components/Input/Input";
import Loader from "../../../../../../components/Loader/Loader";
import MultipleSelect from "../../../../../../components/MultipleSelect/MultipleSelect";
import StandartButton from "../../../../../../components/StandartButton/StandartButton";
import { ContextProvider } from "../../../../../../contextProvider";
import { Project } from "../../../../../../types/Company";
import { ProjectRoles } from "../../../../../../types/Projects";
import { TypeSpecialistFilter } from "../../../../../../types/TypeSpecialist";
import { TypeUserData } from "../../../../../../types/TypeUsers";
import { getTranslation } from "../../../../../../utils/getTranslation";
import { SuccesNotify } from "../../../../../../utils/toaster";
import s from "./InviteBarterUsers.module.css";

export type PickerData = { value: string; lable: string; disabled?: boolean };

type SelectedUser = {
  userId: string;
  email: string;
  name: string;
  surname: string;
  specialization: string[];
  roles: ProjectRoles[];
  allowedSpecializationIds: string[];
};

const PAGE_SIZE = 10;

const InviteBarterUsers: React.FC<{
  setModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  projectData: Project;
  isBarter?: boolean;
}> = ({ setModalVisible, projectData, isBarter }) => {
  const token = localStorage.getItem("token");
  const { userData } = useContext(ContextProvider);

  const [searchValue, setSearchValue] = useState("");
  const [projectSpecializations, setProjectSpecializations] = useState<
    PickerData[]
  >([]);
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
  const [availableSpecialists, setAvailableSpecialists] = useState<
    TypeUserData[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [errorInvites, setErrorInvites] =
    useState<{ email: string; message: string }[]>();

  const [filters, setFilters] = useState<TypeSpecialistFilter>({
    specialistId: {},
  });
  const [filterParams, setFilterParams] = useState({
    mainSpecializations: [] as PickerData[],
    levels: [] as PickerData[],
    languages: [] as PickerData[],
  });
  const [shuffleSeed, setShuffleSeed] = useState<string | undefined>();

  const sessionsCache = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!token) return;
    (async () => {
      const res = await SpecialistApi.getMainSpecializations(token);
      if (res.status && res.mainSpecializations) {
        const specs = projectData.specializations?.length
          ? projectData.specializations
          : res.mainSpecializations;

        setProjectSpecializations(
          specs.map((s) => ({
            value: s._id,
            lable: getTranslation(s.labels),
          })),
        );
      }
    })();
  }, [token, projectData.specializations]);

  const loadSpecialists = useCallback(
    async (append = false) => {
      if (!token || loading) return;
      setLoading(true);

      const permanentlyExcluded = [...selectedUsers.map((u) => u.userId)];

      const alreadyFetched = append
        ? [...permanentlyExcluded, ...availableSpecialists.map((s) => s._id)]
        : permanentlyExcluded;

      const payload = {
        filters: {
          ...filters,
          name: searchValue || undefined,
          email: searchValue || undefined,
          specialistId: {
            ...filters.specialistId,
            isVerified: isBarter ? undefined : true,
          },
          limit: PAGE_SIZE,
          sort: { field: "sessionCount", type: -1 as const },
        },
        alreadyFetched,
        excludeProjectSpecialistsId: projectData._id,
        shuffleSeed: append ? shuffleSeed : undefined,
        calculateSessionsAmount: true,
      };

      try {
        const [usersRes, paramsRes] = await Promise.all([
          SpecialistApi.getFilteredSpecialists(token, payload),

          append
            ? Promise.resolve({ status: true, paramsAvailable: null })
            : SpecialistApi.getFilterAvailableParams(token, {
                filters: payload.filters,
                excludeProjectSpecialistsId: projectData._id,
              }),
        ]);

        if (usersRes.status && usersRes.users) {
          const newUsers = append
            ? [...availableSpecialists, ...usersRes.users]
            : usersRes.users;

          setAvailableSpecialists(newUsers);
          setHasMore(usersRes.users.length === PAGE_SIZE);
          setShuffleSeed(usersRes.shuffleSeed);
        }

        if (!append && paramsRes.status && paramsRes.paramsAvailable) {
          const p = paramsRes.paramsAvailable;

          setFilterParams({
            mainSpecializations:
              p.mainSpecializations?.map((i) => ({
                value: i.mainSpecialization.specialization,
                lable: getTranslation(i.mainSpecialization.labels),
                disabled: !i.choosable,
              })) ?? [],
            languages:
              p.preferredLanguages?.map((i) => ({
                value: i.language,
                lable: i.language,
                disabled: !i.choosable,
              })) ?? [],
            levels:
              p.levels?.levels.map((i) => ({
                value: i.level._id,
                lable: `${getTranslation(
                  i.level.title,
                )} ~ ${i.level.avgPrice.toFixed(0)}$ (${
                  p.mainSpecializations?.find(
                    (el) =>
                      el.mainSpecialization._id ===
                      (i.level.mainSpecialization as unknown as string),
                  )?.mainSpecialization.specialization
                })`,
                disabled: !i.choosable,
              })) ?? [],
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },

    [
      token,
      filters,
      searchValue,
      projectData.specialists,
      projectData.entryCodes,
      selectedUsers,
      availableSpecialists,
      shuffleSeed,
      isBarter,
      loading,
    ],
  );
  useEffect(() => {
    /*   setAvailableSpecialists([]); */
    setHasMore(true);
    setShuffleSeed(undefined);
    loadSpecialists(false);
  }, [filters, searchValue]);

  // Какие специализации можно выбрать этому пользователю
  const getAllowedSpecializationIds = (user: TypeUserData): string[] => {
    if (isBarter) {
      return projectSpecializations.map((s) => s.value);
    }

    const userSpecIds = new Set<string>();
    user.specialistIds?.forEach((profile) => {
      if (profile.mainSpecializations?.[0]) {
        userSpecIds.add(profile.mainSpecializations[0]._id);
      }
    });

    return projectSpecializations
      .filter((s) => userSpecIds.has(s.value))
      .map((s) => s.value);
  };

  const addUser = (user: TypeUserData) => {
    const allowedIds = getAllowedSpecializationIds(user);
    const defaultSpec = allowedIds.length === 1 ? [allowedIds[0]] : [];

    setSelectedUsers((prev) => [
      ...prev,
      {
        userId: user._id,
        email: user.email,
        name: getTranslation(user.name),
        surname: getTranslation(user.surname),
        specialization: defaultSpec,
        roles: isBarter
          ? [ProjectRoles.DEEFAULT_USER, ProjectRoles.SPECIALIST]
          : [ProjectRoles.SPECIALIST],

        allowedSpecializationIds: allowedIds,
      },
    ]);
  };

  const removeUser = (userId: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u.userId !== userId));
  };

  const updateSpecialization = (userId: string, specs: string[]) => {
    setSelectedUsers((prev) =>
      prev.map((u) =>
        u.userId === userId ? { ...u, specialization: specs } : u,
      ),
    );
  };

  const sendInvites = async () => {
    if (!token || selectedUsers.some((u) => u.specialization.length === 0))
      return;

    setLoading(true);
    const invitations = selectedUsers.map((u) => ({
      email: u.email,
      roles: u.roles,
      specializationIds: u.specialization,
    }));

    const res = await ProjectApi.inviteUserToProject(
      token,
      invitations,
      projectData._id,
    );

    setLoading(false);

    if (res.failedInvites?.length) {
      setErrorInvites(
        res.failedInvites.map((i) => ({
          email: i.email,
          message: i.message ?? "Error",
        })),
      );
    } else if (res.status) {
      SuccesNotify("Invitations sent successfully!");
      setModalVisible(false);
    }
  };

  const canInvite =
    selectedUsers.length > 0 &&
    selectedUsers.every((u) => u.specialization.length > 0);

  const getSelectOptionsForUser = (user: SelectedUser): PickerData[] => {
    if (isBarter) return projectSpecializations;
    return projectSpecializations.filter((opt) =>
      user.allowedSpecializationIds.includes(opt.value),
    );
  };

  if (loading && availableSpecialists.length === 0)
    return (
      <div className={s.loader}>
        <Loader size={100} />
      </div>
    );

  if (errorInvites)
    return (
      <div className={s.container}>
        <h2>Failed Invites</h2>
        <div className={s.errorTable}>
          {errorInvites.map((e, i) => (
            <div key={i} className={s.errorRow}>
              <span>{e.email}</span>
              <span>{e.message}</span>
            </div>
          ))}
        </div>
        <StandartButton
          action={() => setModalVisible(false)}
          buttonTitle="Close"
          width="200px"
        />
      </div>
    );

  return (
    <div className={s.container}>
      <h3 className={s.title}>Invite Specialists to Project</h3>

      <div className={s.content}>
        {/* Selected Users */}
        <div className={s.section}>
          <div className={s.sectionTitle}>
            Selected ({selectedUsers.length})
          </div>
          <div className={s.list}>
            {selectedUsers.length === 0 ? (
              <div className={s.empty}>No specialists selected</div>
            ) : (
              selectedUsers.map((user) => (
                <div key={user.userId} className={s.selectedCard}>
                  <div className={s.selectedInfo}>
                    <CircleAvatar
                      userId={user.userId}
                      width="44px"
                      height="44px"
                    />
                    <div>
                      <div className={s.name}>
                        {user.name} {user.surname}
                      </div>
                      <div className={s.email}>{user.email}</div>
                    </div>
                  </div>

                  <MultipleSelect
                    data={getSelectOptionsForUser(user)}
                    value={user.specialization}
                    setValue={(v) =>
                      updateSpecialization(user.userId, v as string[])
                    }
                    multiplie
                    checkBox
                    border={
                      user.specialization.length
                        ? "1px solid #555"
                        : "1px solid red"
                    }
                    width="340px"
                    placeholder={
                      user.allowedSpecializationIds.length === 0
                        ? "No suitable specialization"
                        : isBarter
                          ? "Any from project"
                          : "Select specialization"
                    }
                    disabled={user.allowedSpecializationIds.length === 0}
                  />

                  <CloseIcon
                    className={s.closeIcon}
                    onClick={() => removeUser(user.userId)}
                  />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Available Specialists */}
        <div className={s.section}>
          <div className={s.sectionTitle}>Available Specialists</div>

          <div className={s.filters}>
            <Input
              inputValue={searchValue}
              onChangeInput={setSearchValue}
              placeholder="Search..."
              isSearchinput={!searchValue}
              isCloseIcon={!!searchValue}
              closeFunction={() => setSearchValue("")}
              width="25%"
            />
            <MultipleSelect
              data={[
                ...filterParams.mainSpecializations,
                { lable: "All specializations", value: "initial" },
              ]}
              value={filters.specialistId?.mainSpecializations ?? []}
              setValue={(v) =>
                setFilters((p) => ({
                  ...p,
                  specialistId: {
                    ...p.specialistId,
                    mainSpecializations:
                      v && v !== "initial" ? [v as string] : undefined,
                  },
                }))
              }
              lable="Specialization"
              multiplie={false}
              width="25%"
            />
            <MultipleSelect
              data={[
                ...filterParams.levels,
                { lable: "All level", value: "initial" },
              ]}
              value={filters.specialistId?.level ?? []}
              setValue={(v) =>
                setFilters((p) => ({
                  ...p,
                  specialistId: {
                    ...p.specialistId,
                    level: v && v !== "initial" ? [v as string] : undefined,
                  },
                }))
              }
              lable="Level"
              multiplie={false}
              width="25%"
            />
            <MultipleSelect
              data={[
                ...filterParams.languages,
                { lable: "All langauges", value: "initial" },
              ]}
              value={filters.preferredLanguages ?? ["initial"]}
              setValue={(v) => {
                const value =
                  !v.length ||
                  (v.length === 1 && v.includes("initial")) ||
                  v[v.length - 1] === "initial"
                    ? undefined
                    : (v as string[]).filter((el) => el !== "initial");
                setFilters((p) => ({
                  ...p,
                  preferredLanguages: value as string[],
                }));
              }}
              lable="Languages"
              multiplie={true}
              width="25%"
            />
          </div>

          <div className={s.list}>
            {availableSpecialists.map((spec) => {
              const allowedCount = getAllowedSpecializationIds(spec).length;

              return (
                <div key={spec._id} className={s.card}>
                  <CircleAvatar userId={spec._id} width="56px" height="56px" />
                  <div className={s.info}>
                    <div className={s.name}>
                      {getTranslation(spec.name)} {getTranslation(spec.surname)}
                    </div>
                    <div className={s.email}>{spec.email}</div>
                    <div className={s.sessions}>
                      Sessions completed:{" "}
                      {spec.specialistIds &&
                        spec.specialistIds.every(
                          (el) => el.mainSpecializations?.length,
                        ) &&
                        spec.specialistIds.map((item) => (
                          <strong>
                            {item.mainSpecializations[0].specialization} -{" "}
                            {item.sessionCount};{" "}
                          </strong>
                        ))}
                      {!isBarter && allowedCount === 0 && (
                        <span className={s.warning}>
                          {" "}
                          (No matching specialization)
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    className={s.addBtn}
                    onClick={() => addUser(spec)}
                    disabled={
                      selectedUsers.some((u) => u.userId === spec._id) ||
                      (!isBarter && allowedCount === 0)
                    }
                  >
                    {selectedUsers.some((u) => u.userId === spec._id)
                      ? "Added"
                      : !isBarter && allowedCount === 0
                        ? "Not eligible"
                        : "Add"}
                  </button>
                </div>
              );
            })}

            {loading && <div className={s.loading}>Loading more...</div>}

            {hasMore && !loading && (
              <button
                className={s.showMore}
                onClick={() => loadSpecialists(true)}
              >
                Show More
              </button>
            )}

            {!hasMore && availableSpecialists.length > 0 && (
              <div className={s.noMore}>No more specialists</div>
            )}

            {availableSpecialists.length === 0 && !loading && (
              <div className={s.empty}>No specialists found</div>
            )}
          </div>
        </div>
      </div>

      <div className={s.actions}>
        <StandartButton
          action={sendInvites}
          buttonTitle="Send Invites"
          width="240px"
          disabled={!canInvite || loading}
          tooltipTitle={
            !canInvite ? "Select specialization for all users" : undefined
          }
        />
        <button className={s.cancel} onClick={() => setModalVisible(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default InviteBarterUsers;
